import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
} from 'react';
import { AccountInfoResult, ContractResult } from '@block-explorer/entities';

type DeployActionDataContextType = {
  getAccountInfo: <T>(publicKey: string) => T | null | undefined;
  getPublicKeyAccountHash: (accountHash: string) => string | null | undefined;
  getContractPackagePath: (hash: string) => string | null;
  getAccountPath?: (hash: string) => string | null;
  getContractInfoByHash?: (
    contractHash: string
  ) => ContractResult | null | undefined;
};

export const deployActionDataContext =
  createContext<DeployActionDataContextType>({
    getAccountInfo: () => null,
    getPublicKeyAccountHash: () => null,
    getContractInfoByHash: () => null,
    getContractPackagePath: () => null,
    getAccountPath: () => null,
  });

const { Provider: DeployActionDataContextProvider } = deployActionDataContext;

export const useDeployActionDataContext = () => {
  return useContext(deployActionDataContext);
};

type DeployActionDataProviderProps = {
  getAccountInfo: <T>(publicKey: string) => T | null | undefined;
  getContractInfoByHash?: (
    contractHash: string
  ) => ContractResult | null | undefined;
  getContractPackagePath: (hash: string) => string | null;
  getAccountPath?: (hash: string) => string | null;
};

export const DeployActionDataProvider = (
  props: PropsWithChildren<DeployActionDataProviderProps>
) => {
  const {
    getAccountInfo,
    getAccountPath,
    getContractPackagePath,
    getContractInfoByHash,
    children,
  } = props;

  const getPublicKeyAccountHash = useCallback(
    (accountHash: string): string | undefined => {
      const publicKeyByHash =
        getAccountInfo<AccountInfoResult>(accountHash)?.public_key;

      return publicKeyByHash ? publicKeyByHash : undefined;
    },
    [getAccountInfo]
  );

  return (
    <DeployActionDataContextProvider
      value={{
        getAccountInfo,
        getPublicKeyAccountHash,
        getAccountPath,
        getContractPackagePath,
        getContractInfoByHash,
      }}
    >
      {children}
    </DeployActionDataContextProvider>
  );
};
